import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { errors as mockErrors } from "../../data/errorMockData";
import ErrorFilters from "./components/ErrorFilters";
import ErrorTable from "./components/ErrorTable";
import ErrorAnalysis from "./components/ErrorAnalysis";
import { FiAlertOctagon, FiAlertTriangle, FiCheckCircle, FiInfo, FiLogOut, FiArrowLeft } from "react-icons/fi";
import api from "../../helper/errorMonitorApi";
import { isErrorMonitorAuthed, logoutErrorMonitor } from "./errorAuth";

const ErrorDashboard = () => {
  const [filters, setFilters] = useState({
    search: "",
    vendor: "",
    module: "",
    severity: "",
    status: "",
    frequent : false
  });

  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);

  const normalizeSeverity = (sev) => {
    const s = String(sev || "").toUpperCase();
    if (s === "HIGH") return "High";
    if (s === "MEDIUM") return "Medium";
    if (s === "LOW") return "Low";
    return "Medium";
  };

  const normalizeStatus = (resolved) => (resolved ? "Resolved" : "Open");

  const formatDateTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString();
  };

  const mapApiLogToUi = (log) => {
    const endpoint = log?.requestPath || log?.route || "";
    const errorMessage = log?.lastErrorMessage || log?.reason || "Unknown error";

    return {
      id: log?._id,
      userId : log?.userId,
      vendor: log?.vendor || "unknown",
      module: log?.module || "Flight",
      route: log?.route,
      endpoint,
      errorMessage,
      occurrenceCount : log?.occurrenceCount,
      severity: normalizeSeverity(log?.severity),
      status: normalizeStatus(log?.resolved),
      resolved: log?.resolved,
      createdAt: formatDateTime(log?.createdAt),
      detailsMessage: log?.reason || log?.lastErrorMessage || "",
      functionName: Array.isArray(log?.tags) ? log.tags?.[1] : "",
      ...log,
      errorJson: {
        httpStatus: log?.httpStatus,
        severity: log?.severity,
        errorCode: log?.errorCode,
        resolved: log?.resolved,
        requestMethod: log?.requestMethod,
        requestPath: log?.requestPath,
        route: log?.route,
        fileName: log?.fileName,
        lineNumber: log?.lineNumber,
        occurrenceCount: log?.occurrenceCount,
        firstOccurredAt: log?.firstOccurredAt,
        lastOccurredAt: log?.lastOccurredAt,
        lastErrorMessage: log?.lastErrorMessage,
        lastErrorStack: log?.lastErrorStack,
        tags: log?.tags,
        userId: log?.userId,
        isHighAlert: log?.isHighAlert,
      },
    };
  };

  const fetchErrorLogs = async () => {
    try {
      const res = await api.get("/api-error-logs");
      const arr = Array.isArray(res.data?.data) ? res.data.data : [];
      return arr.map(mapApiLogToUi);
    } catch (e) {
      throw e;
    }
  };

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const rows = await fetchErrorLogs();
      console.log(rows)
      setData(rows);
    } catch (e) {
      setData([]);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isErrorMonitorAuthed()) {
      load(false);
    }
  }, []);

  const handleResolve = async (id) => {
    if (!id || resolving) return;
    setResolving(true);
    try {
      const resp = await api.post(`/api-error-logs/${id}/resolve`);
      if (resp.data?.status === 'success'){
        load();
      }
      return true;
    } catch (e) {
      console.error("Failed to resolve error", e);
      return false;
    } finally {
      setResolving(false);
    }
  };

  const filtered = useMemo(() => {
    let result = data.filter((item) => {
      if (filters.vendor && item.vendor !== filters.vendor) return false;
      if (filters.module && item.module !== filters.module) return false;
      if (filters.severity && item.severity !== filters.severity) return false;
      if (filters.status && item.status !== filters.status) return false;
  
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const text = `${item.errorMessage} ${item.endpoint} ${item.id}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
  
      return true;
    });
    if (filters?.frequent) {
      result = [...result].sort((a, b) => (b.occurrenceCount || 0) - (a.occurrenceCount || 0));
    }
  
    return result;
  }, [filters, data]);

  const summary = useMemo(() => {
    const total = data.length;
    const high = data.filter((e) => String(e.severity).toLowerCase() === "high").length;
    const medium = data.filter((e) => String(e.severity).toLowerCase() === "medium").length;
    const low = data.filter((e) => String(e.severity).toLowerCase() === "low").length;
    const resolved = data.filter((e) => e.resolved).length;
    return { total, high, medium, low, resolved };
  }, [data]);

  const handleRefresh = () => {
    if (refreshing) return;
    load(true);
  };

  const handleLogout = () => {
    logoutErrorMonitor();
    navigate("/error-monitor/login", { replace: true });
  };

  return (
    <div className="hw-page--width">
      <div className="em-page">
        <div className="em-header">
          <div className="d-flex-column">
            <h1 className="h3 em-title">Error Monitoring</h1>
            <p className="em-subtitle">
              Monitor integration errors across Flights, Hotels, Excursions and Payments.
            </p>
          </div>
          <div className="d-flex hw-gapxy10">
            <button
              type="button"
              className="em-btn"
              onClick={() => navigate("/flight/search")}
            >
              <FiArrowLeft />
              Back
            </button>
            <button
              type="button"
              className="em-btn"
              onClick={handleLogout}
            >
              <FiLogOut />
              Logout
            </button>
          </div>
        </div>

        <div className="em-grid">
          <div className="em-card">
            <div className="em-card__top">
              <div>
                <div className="em-card__label">Total Errors</div>
                <div className="em-card__value">{summary.total}</div>
              </div>
              <span className="em-icon">
                <FiAlertOctagon size={18} />
              </span>
            </div>
          </div>

          <div className="em-card">
            <div className="em-card__top">
              <div>
                <div className="em-card__label">High Severity</div>
                <div className="em-card__value">{summary.high}</div>
              </div>
              <span className="em-icon em-icon--danger">
                <FiAlertTriangle size={18} />
              </span>
            </div>
          </div>

          <div className="em-card">
            <div className="em-card__top">
              <div>
                <div className="em-card__label">Medium Severity</div>
                <div className="em-card__value">{summary.medium}</div>
              </div>
              <span className="em-icon em-icon--warning">
                <FiInfo size={18} />
              </span>
            </div>
          </div>

          <div className="em-card">
            <div className="em-card__top">
              <div>
                <div className="em-card__label">Low Severity</div>
                <div className="em-card__value">{summary.low}</div>
              </div>
              <span className="em-icon em-icon--success">
                <FiCheckCircle size={18} />
              </span>
            </div>
          </div>
        </div>

        <ErrorAnalysis data={data} refreshing={refreshing} onRefresh={handleRefresh} loading={loading} />

        <ErrorFilters data={data} filters={filters} onChange={setFilters} />

        <ErrorTable data={filtered} onResolved={handleResolve} resolving={resolving} />
      </div>
    </div>
  );
};

export default ErrorDashboard;

